import { useQuery } from '@tanstack/react-query';
import { BarChart3, CheckCircle2, ClipboardList, FileText, GlassWater, Globe, Home, LayoutList, LogOut, Menu, MessageSquare, Plus, Search, Shapes, Shield, User, Users, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser, useLogout } from '../hooks/useAuth';
import { facilityTypesApi, usersApi } from '../lib/api-client';
import { hasPermission } from '../lib/permissions';
import { cn } from '../lib/utils';
import logoRw from '../logo-rw.png';
import SurveyReminderModal from './SurveyReminderModal';
import { Input } from './ui/Input';

interface FacilityTypeCatalogItem {
  id: string;
  name: string;
  nameKinyarwanda: string;
  nameFrench: string;
  code: string;
  questionnaires: {
    hygiene: { id?: string; name?: string; available: boolean };
    compliance: { id?: string; name?: string; available: boolean };
  };
}

const navigation = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: BarChart3, requiredPermission: 'ANALYTICS_READ' },
  { nameKey: 'nav.facilities', href: '/facilities', icon: Home, requiredPermission: 'FACILITIES_READ' },
  { nameKey: 'nav.assessments', href: '/assessments', icon: FileText, requiredPermission: 'ASSESSMENTS_READ' },
  { nameKey: 'nav.users', href: '/users', icon: Users, requiredPermission: 'USERS_READ' },
  { nameKey: 'nav.facility_types', href: '/facility-types', icon: Shapes, requiredPermission: 'FACILITIES_CREATE' },
  { nameKey: 'nav.question_management', href: '/questions', icon: ClipboardList, requiredPermission: 'ASSESSMENTS_QUESTIONNAIRES' },
  { nameKey: 'nav.roles', href: '/roles', icon: Shield, requiredPermission: 'ROLES_MANAGEMENT' },
  { nameKey: 'nav.appeals', href: '/appeals', icon: MessageSquare, requiredPermission: 'ASSESSMENTS_READ' },
  { nameKey: 'nav.surveys', href: '/surveys', icon: LayoutList, requiredPermission: 'SURVEYS_READ' },
  { nameKey: 'nav.beverage_authorizations', href: '/beverage-authorizations', icon: GlassWater, requiredPermission: 'BEVERAGE_AUTHORIZATIONS_READ' },
];

const formatRole = (role: unknown) => {
  const roleCode = typeof role === 'string' ? role : (role as any)?.code ?? (role as any)?.name;
  if (!roleCode || typeof roleCode !== 'string') return 'N/A';

  return roleCode
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const IS_STAGING = window.location.hostname.includes('kwikkoders.com');

export default function Layout() {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const location = useLocation();
  const user = getCurrentUser();
  const logout = useLogout();
  const { locale, setLocale, dictionary } = useLocale();

  type TLocale = 'en' | 'rw';

  const userMenuRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Fetch user's location hierarchy
  const { data: locationHierarchy } = useQuery({
    queryKey: ['user-location-hierarchy'],
    queryFn: () => usersApi.getMyLocationHierarchy(),
    enabled: !!user,
  });

  // Fetch facility types catalog (lazy – only when modal is opened)
  const { data: catalogItems, isLoading: catalogLoading } = useQuery({
    queryKey: ['facility-types-catalog'],
    queryFn: () => facilityTypesApi.getAll() as Promise<FacilityTypeCatalogItem[]>,
    enabled: catalogOpen,
    staleTime: 1000 * 60 * 10,
  });

  // Responsive breakpoint detection
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsMedium(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    setUserMenuOpen(false);
    setLanguageMenuOpen(false);
    setMobileMenuOpen(false);
    setCatalogOpen(false);
  }, [location.pathname]);


  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        setLanguageMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);


  const filteredNavigation = navigation.filter(item => {
    if (item.requiredPermission) {
      if (!hasPermission(user as any, item.requiredPermission)) return false;
    }
    return true;
  });


  const formatLocationHierarchy = useCallback(() => {
    if (!locationHierarchy) return null;

    const locations = [
      locationHierarchy.provinceName,
      locationHierarchy.districtName,
      locationHierarchy.sectorName,
      locationHierarchy.cellName,
      locationHierarchy.villageName,
    ].filter(Boolean);

    return locations.join(' > ');
  }, [locationHierarchy]);

  const primaryNavItems = filteredNavigation.slice(0, 2);
  const hasMoreItems = filteredNavigation.length > 2;

  return (
    <div className='min-h-screen bg-background'>
      {/* Top Navigation Bar */}
      <nav className='sticky top-0 z-[100] bg-white border-b border-border'>
        {IS_STAGING && (
          <div className='bg-amber-400 text-stone-900 text-center text-xs font-semibold py-1.5 px-4 leading-tight'>
            ⚠️ This environment is for testing and training purposes only. Inspections done here are not considered in official reporting.&nbsp;&nbsp;|&nbsp;&nbsp;Iyi ni link y'igerageza no guhuguriraho imikoreshereze ya systeme. Amakuru wandikamo ntabwo afatwa nk'ayukuri mu gihe cyo gutanga raporo.
          </div>
        )}
        <div className='px-4 sm:px-6 max-w-[1600px] mx-auto'>
          <div className='flex items-center gap-6 min-h-16'>
            {/* Logo */}
            <Link to='/dashboard' className='flex items-center gap-3 flex-shrink-0' aria-label='Home'>
              <img src={logoRw} alt='Republic of Rwanda' className='w-10 h-auto flex-shrink-0' />
              <div className='hidden sm:flex flex-col'>
                <span className='text-sm font-bold text-foreground leading-tight whitespace-nowrap'>
                  NHIP
                  {import.meta.env.VITE_APP_ENV === 'development' && (
                    <span className='ml-2 text-xs px-1.5 py-0.5 rounded-md bg-yellow-100 text-yellow-700 border border-yellow-300 font-medium'>Testing</span>
                  )}
                </span>
                <span className='text-xs text-muted-foreground leading-tight whitespace-nowrap'>Ministry of Health · Rwanda</span>
              </div>
            </Link>

            {/* Desktop Nav Links */}
            <div className='hidden lg:flex items-center gap-0.5 flex-1 h-16 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
              {filteredNavigation.map(item => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'px-2.5 h-full flex items-center text-sm font-medium border-b-2 transition-colors duration-150 whitespace-nowrap',
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {dictionary[item.nameKey]}
                  </Link>
                );
              })}
            </div>

            {/* Right Side */}
            <div className='flex items-center gap-3 flex-shrink-0 ml-auto'>
              {/* Catalog Button */}
              <button
                onClick={() => { setCatalogOpen(true); setCatalogSearch(''); }}
                className='hidden lg:flex p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-150'
                title='Facility Types Catalog'
                aria-label='Open facility types catalog'
              >
                <LayoutList className='h-5 w-5' />
              </button>

              {/* Language Switcher */}
              <div className='hidden lg:block relative' ref={languageMenuRef}>
                <button
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  className='p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-150'
                  title={dictionary['nav.change_language']}
                  aria-label='Change language'
                  aria-expanded={languageMenuOpen}
                  aria-haspopup='true'
                >
                  <Globe className='h-5 w-5' />
                </button>
                {languageMenuOpen && (
                  <div
                    className='absolute right-0 mt-2 w-44 bg-card border border-border rounded-lg shadow-lg z-50'
                    role='menu'
                  >
                    <div className='py-1'>
                      {[
                        { code: 'en', flag: '🇺🇸', name: 'English' },
                        { code: 'rw', flag: '🇷🇼', name: 'Kinyarwanda' },
                      ].map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLocale(lang.code as TLocale);
                            setLanguageMenuOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-150',
                            locale === lang.code
                              ? 'bg-secondary text-primary font-medium'
                              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                          )}
                          role='menuitem'
                        >
                          <span className='text-lg'>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* New Assessment Button */}
              <Link
                to='/assessments/new'
                className='hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors duration-150 shadow-sm'
                aria-label='Create new assessment'
              >
                <Plus className='h-4 w-4' />
                <span className='whitespace-nowrap'>{dictionary['nav.new_assessment'] || '+ New Assessment'}</span>
              </Link>

              {/* User Menu */}
              <div className='relative' ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className='flex items-center gap-2'
                  aria-label='User menu'
                  aria-expanded={userMenuOpen}
                  aria-haspopup='true'
                >
                  <div className='hidden sm:flex flex-col text-right'>
                    <span className='text-sm font-semibold text-foreground leading-tight'>{user?.name}</span>
                    <span className='text-xs text-muted-foreground leading-tight'>{user?.email}</span>
                  </div>
                  <div className='h-9 w-9 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm flex-shrink-0'>
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                </button>

                {/* User Dropdown */}
                {userMenuOpen && (
                  <div
                    className='absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg z-50'
                    role='menu'
                  >
                    <div className='p-3 border-b border-border'>
                      <p className='text-sm font-medium text-foreground truncate'>{user?.name}</p>
                      <p className='text-xs text-muted-foreground truncate'>
                        {user?.assignedRole?.name
                          ? user.assignedRole.name
                          : user?.assignedRole?.code
                            ? formatRole(user.assignedRole.code)
                            : user?.role
                              ? formatRole(user.role)
                              : 'N/A'}
                      </p>
                    </div>
                    <div className='py-1'>
                      <Link
                        to='/profile'
                        className='flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-150'
                        onClick={() => setUserMenuOpen(false)}
                        role='menuitem'
                      >
                        <User className='h-4 w-4' />
                        {dictionary['nav.profile'] || 'Profile'}
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setUserMenuOpen(false);
                        }}
                        className='w-full flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 hover:text-destructive transition-colors duration-150'
                        role='menuitem'
                      >
                        <LogOut className='h-4 w-4' />
                        {dictionary['nav.logout'] || 'Logout'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Page content with bottom padding on mobile/tablet */}
      <main className={cn('min-h-[calc(100vh-4rem)]', (isMobile || isMedium) && 'pb-20')}>
        <Outlet />
      </main>

      {/* Auto-reminder for surveys with pending responses in the user's jurisdiction */}
      {hasPermission(user as any, 'SURVEYS_READ') && <SurveyReminderModal />}

      {/* Bottom Navigation (Mobile/Tablet Only) */}
      {(isMobile || isMedium) && (
        <>
          <nav className='fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg'>
            <div className='flex items-center justify-around h-16 px-2'>
              {/* Primary Navigation Items (Dashboard & Facilities) */}
              {primaryNavItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.href);

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex flex-col items-center justify-center flex-1 h-full transition-colors duration-150 px-1',
                      isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className={cn('h-6 w-6 mb-1', isActive && 'text-primary')} />
                    <span className={cn('text-xs font-medium text-center', isActive && 'text-primary')}>
                      {dictionary[item.nameKey]}
                    </span>
                  </Link>
                );
              })}

              {/* New Assessment Floating Button */}
              <Link
                to='/assessments/new'
                className='flex flex-col items-center justify-center flex-1 h-full transition-colors duration-150'
                aria-label='Create new assessment'
              >
                <div className='w-14 h-14 -mt-8 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center shadow-lg border-4 border-background'>
                  <Plus className='h-7 w-7 text-white' />
                </div>
                <span className='text-xs font-medium text-primary mt-1'>
                  {isMobile ? 'New' : dictionary['nav.new_assessment'] || 'New'}
                </span>
              </Link>

              {/* More Menu Button */}
              {hasMoreItems && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={cn(
                    'flex flex-col items-center justify-center flex-1 h-full transition-colors duration-150 px-1',
                    mobileMenuOpen ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  )}
                  aria-label='More menu'
                  aria-expanded={mobileMenuOpen}
                >
                  <Menu className={cn('h-6 w-6 mb-1', mobileMenuOpen && 'text-primary')} />
                  <span className={cn('text-xs font-medium', mobileMenuOpen && 'text-primary')}>More</span>
                </button>
              )}
            </div>
          </nav>

          {/* Full Screen More Menu Overlay */}
          {mobileMenuOpen && (
            <div
              className='fixed inset-0 bg-black/50 z-[110] backdrop-blur-sm'
              onClick={() => setMobileMenuOpen(false)}
            >
              <div
                ref={mobileMenuRef}
                className='absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl shadow-2xl max-h-[70vh] overflow-y-auto'
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className='sticky top-0 bg-card border-b border-border px-4 py-3 flex items-center justify-between rounded-t-3xl'>
                  <h2 className='text-base font-semibold text-foreground'>Menu</h2>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className='p-2 hover:bg-accent rounded-full transition-colors'
                    aria-label='Close menu'
                  >
                    <X className='h-5 w-5' />
                  </button>
                </div>

                {/* Menu Items */}
                <div className='px-4 py-3 space-y-1'>
                  {filteredNavigation.slice(2).map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname.startsWith(item.href);

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg transition-all duration-200',
                          isActive ? 'bg-secondary text-primary' : 'hover:bg-accent'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div
                          className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                            isActive ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          )}
                        >
                          <Icon className='h-5 w-5' />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className={cn('font-medium text-sm', isActive ? 'text-primary' : 'text-foreground')}>
                            {dictionary[item.nameKey]}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {/* Bottom padding for safe area */}
                <div className='h-4' />
              </div>
            </div>
          )}
        </>
      )}
      {/* Facility Types Catalog Modal */}
      {catalogOpen && (
        <div
          className='fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-16 px-4'
          onClick={() => setCatalogOpen(false)}
        >
          <div
            className='bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col'
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className='flex items-center justify-between px-5 py-4 border-b border-gray-200'>
              <div className='flex items-center gap-2'>
                <LayoutList className='h-5 w-5 text-primary' />
                <h2 className='text-base font-semibold text-gray-900'>Facility Types Catalog</h2>
              </div>
              <button
                onClick={() => setCatalogOpen(false)}
                className='p-1.5 hover:bg-gray-100 rounded-full transition-colors'
                aria-label='Close catalog'
              >
                <X className='h-5 w-5 text-gray-500' />
              </button>
            </div>

            {/* Search */}
            <div className='px-5 py-3 border-b border-gray-100'>
              <Input
                type='text'
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
                placeholder='Search facility types...'
                inputPrefix={<Search className='h-4 w-4 ml-3 text-gray-400 flex-shrink-0' />}
                autoFocus
              />
            </div>

            {/* Legend */}
            <div className='flex items-center gap-4 px-5 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500'>
              <span className='flex items-center gap-1'><CheckCircle2 className='h-3.5 w-3.5 text-green-600' /> Available</span>
              <span className='flex items-center gap-1'><XCircle className='h-3.5 w-3.5 text-gray-300' /> Not available</span>
            </div>

            {/* Table */}
            <div className='overflow-y-auto flex-1'>
              {catalogLoading ? (
                <div className='flex items-center justify-center py-16 text-sm text-gray-500'>
                  Loading...
                </div>
              ) : (() => {
                const filtered = (catalogItems ?? []).filter(ft =>
                  ft.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  ft.nameKinyarwanda.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  ft.code.toLowerCase().includes(catalogSearch.toLowerCase())
                );
                return filtered.length === 0 ? (
                  <div className='flex items-center justify-center py-16 text-sm text-gray-400'>
                    No facility types found.
                  </div>
                ) : (
                  <table className='w-full text-sm'>
                    <thead className='sticky top-0 bg-gray-50 border-b border-gray-200'>
                      <tr>
                        <th className='text-left px-5 py-2.5 font-medium text-gray-600 w-1/2'>Facility Type</th>
                        <th className='text-center px-3 py-2.5 font-medium text-gray-600 w-1/4'>Hygiene</th>
                        <th className='text-center px-3 py-2.5 font-medium text-gray-600 w-1/4'>Compliance</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100'>
                      {filtered.map(ft => (
                        <tr key={ft.id} className='hover:bg-gray-50 transition-colors'>
                          <td className='px-5 py-3'>
                            <p className='font-medium text-gray-900 leading-tight'>{ft.name}</p>
                            {ft.nameKinyarwanda && ft.nameKinyarwanda !== ft.name && (
                              <p className='text-xs text-gray-400 mt-0.5 truncate max-w-xs'>{ft.nameKinyarwanda}</p>
                            )}
                          </td>
                          <td className='px-3 py-3 text-center'>
                            {ft.questionnaires?.hygiene?.available ? (
                              <span title={ft.questionnaires.hygiene.name} className='inline-flex flex-col items-center gap-0.5'>
                                <CheckCircle2 className='h-4 w-4 text-green-600' />
                                {ft.questionnaires.hygiene.name && (
                                  <span className='text-xs text-gray-500 max-w-[120px] truncate leading-tight'>
                                    {ft.questionnaires.hygiene.name}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <XCircle className='h-4 w-4 text-gray-300 mx-auto' />
                            )}
                          </td>
                          <td className='px-3 py-3 text-center'>
                            {ft.questionnaires?.compliance?.available ? (
                              <span title={ft.questionnaires.compliance.name} className='inline-flex flex-col items-center gap-0.5'>
                                <CheckCircle2 className='h-4 w-4 text-green-600' />
                                {ft.questionnaires.compliance.name && (
                                  <span className='text-xs text-gray-500 max-w-[120px] truncate leading-tight'>
                                    {ft.questionnaires.compliance.name}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <XCircle className='h-4 w-4 text-gray-300 mx-auto' />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Footer count */}
            {!catalogLoading && catalogItems && (
              <div className='px-5 py-2.5 border-t border-gray-100 text-xs text-gray-400'>
                {catalogItems.filter(ft =>
                  ft.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  ft.nameKinyarwanda.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                  ft.code.toLowerCase().includes(catalogSearch.toLowerCase())
                ).length} of {catalogItems.length} types
                &nbsp;·&nbsp;
                {catalogItems.filter(ft => ft.questionnaires?.hygiene?.available).length} with hygiene
                &nbsp;·&nbsp;
                {catalogItems.filter(ft => ft.questionnaires?.compliance?.available).length} with compliance
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
